// src/components/user/Masivo/MasivoWizard.jsx
import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { ChevronLeft, PackageCheck, AlertTriangle } from 'lucide-react';
import DateSelector from './DateSelector';
import FileUploader from './FileUploader';
import ValidationProgress from './ValidationProgress';
import DeliveriesReview from './DeliveriesReview';
import SubmissionProgress from './SubmissionProgress';
import { parseExcelFile, generateExcelFile } from '../../../utils/excelParser';
import { validateAllDeliveries } from '../../../utils/deliveryValidator';

const API_URL = process.env.REACT_APP_API_URL;

function MasivoWizard({
  userId,
  setActiveComponent,
  returnComponent = 'Analytics',
  controlTowerComponent = 'ControlTower',
  title = 'Carga Masiva',
  description = 'Sube un archivo Excel con múltiples entregas',
  successPrimaryLabel = 'Ver Mis Entregas',
  controlTowerLabel = 'Torre de Control',
}) {
  // Step state
  const [currentStep, setCurrentStep] = useState('date'); // date | upload | validating | review | submitting | complete

  // Data state
  const [selectedDate, setSelectedDate] = useState('');
  const [selectedFile, setSelectedFile] = useState(null);
  const [validatedDeliveries, setValidatedDeliveries] = useState([]);
  const [validDistricts, setValidDistricts] = useState({});
  const [userProducts, setUserProducts] = useState([]);
  
  // Submission state
  const [submissionResult, setSubmissionResult] = useState(null);
  const [submissionProgress, setSubmissionProgress] = useState({
    total: 0,
    created: 0,
    messagesSent: 0,
    currentStage: 'saving'
  });

  // Error state
  const [error, setError] = useState(null);

  // Load districts and user products on mount
  useEffect(() => {
    loadDistricts();
    loadUserProducts();
  }, [userId]);

  async function loadDistricts() {
    try {
      const response = await axios.get(`${API_URL}/api/masivo/districts`);
      setValidDistricts(response.data);
    } catch (err) {
      console.error('Error loading districts:', err);
      // Fallback to empty object
      setValidDistricts({ districts_by_zone: {}, all_districts: [] });
    }
  }

  async function loadUserProducts() {
    try {
      const response = await axios.get(`${API_URL}/api/masivo/user-products/${userId}`);
      setUserProducts(response.data.products || []);
    } catch (err) {
      console.error('Error loading user products:', err);
      setUserProducts([]);
    }
  }

  function handleDateChange(date) {
    setSelectedDate(date);
    // If date changes after file upload, stay on upload step
    if (currentStep !== 'date') {
      setCurrentStep('upload');
      setValidatedDeliveries([]);
    } else if (date) {
      // Move to upload step when date is selected
      setCurrentStep('upload');
    }
  }

  function handleFileSelect(file) {
    setSelectedFile(file);
    setError(null);
    // Start validation
    validateFile(file);
  }

  function handleClearFile() {
    setSelectedFile(null);
    setValidatedDeliveries([]);
    setError(null);
    setCurrentStep('upload');
  }

  async function validateFile(file) {
    try {
      setCurrentStep('validating');
      setError(null);

      // Parse Excel
      const parsed = await parseExcelFile(file);
      
      if (parsed.rows.length === 0) {
        setError('El archivo Excel está vacío');
        setCurrentStep('upload');
        return;
      }

      // Validate all rows
      const validated = validateAllDeliveries(
        parsed.rows, 
        validDistricts
      );

      setValidatedDeliveries(validated.rows);
      setCurrentStep('review');

    } catch (err) {
      console.error('Error validating file:', err);
      setError(err.message || 'Error al procesar el archivo');
      setCurrentStep('upload');
    }
  }

  function handleUpdateDelivery(updatedDelivery) {
    setValidatedDeliveries(prev =>
      prev.map(d => 
        d.rowNumber === updatedDelivery.rowNumber ? updatedDelivery : d
      )
    );
  }

  function handleDeleteDelivery(rowNumber) {
    setValidatedDeliveries(prev =>
      prev.filter(d => d.rowNumber !== rowNumber)
    );
  }

  async function handleProceed() {
    try {
      setCurrentStep('submitting');
      setError(null);

      // Get only valid deliveries
      const validDeliveries = validatedDeliveries.filter(d => d.isValid);

      // Prepare data for backend (add selected date to each row)
      const deliveriesData = validDeliveries.map(d => ({
        ...d.data,
        'Fecha': formatDateForBackend(selectedDate)
      }));

      // Generate Excel blob from validated data
      const excelBlob = generateExcelFile(deliveriesData, 'entregas_validadas.xlsx');

      // Prepare form data
      const formData = new FormData();
      formData.append('file', excelBlob, 'entregas_validadas.xlsx');
      formData.append('user_id', userId);

      // Set initial progress
      setSubmissionProgress({
        total: validDeliveries.length,
        created: 0,
        messagesSent: 0,
        currentStage: 'saving'
      });

      // Submit to backend
      const response = await axios.post(
        `${API_URL}/api/delivery_points/bulk_upload`,
        formData,
        {
          headers: { 'Content-Type': 'multipart/form-data' }
        }
      );

      // Update progress with results
      setSubmissionProgress({
        total: validDeliveries.length,
        created: response.data.created || 0,
        messagesSent: response.data.send_ok || 0,
        currentStage: 'complete'
      });

      setSubmissionResult(response.data);
      setCurrentStep('complete');

    } catch (err) {
      console.error('Error submitting deliveries:', err);
      setError(err.response?.data?.error || 'Error al crear las entregas');
      setCurrentStep('review');
    }
  }

  function formatDateForBackend(dateString) {
    // Convert YYYY-MM-DD to DD/MM/YYYY for backend
    const [year, month, day] = dateString.split('-');
    return `${day}/${month}/${year}`;
  }

  function handleReset() {
    setCurrentStep('date');
    setSelectedDate('');
    setSelectedFile(null);
    setValidatedDeliveries([]);
    setSubmissionResult(null);
    setError(null);
  }

  function handleBackButton() {
    // If on date selection step, go back to the parent deliveries view
    if (currentStep === 'date') {
      setActiveComponent(returnComponent);
    } else {
      // From any other step, go back to date selection
      setCurrentStep('date');
      setSelectedFile(null);
      setValidatedDeliveries([]);
      setError(null);
    }
  }

  return (
    <div className="min-h-screen flex flex-col bg-gradient-to-br from-slate-50 via-white to-slate-50">
      {/* Fixed Header */}
      <div className="flex-shrink-0 bg-white border-b border-slate-200 shadow-sm">
        <div className="max-w-4xl mx-auto px-4 py-4 sm:px-6 lg:px-8">
          <button
            onClick={handleBackButton}
            className="flex items-center gap-2 text-slate-700 hover:text-brand-primary transition-colors duration-200 font-montserrat"
          >
            <ChevronLeft className="w-5 h-5" />
            <span className="text-sm sm:text-base">
              {currentStep === 'date' ? 'Volver a Mis Entregas' : 'Cambiar Fecha'}
            </span>
          </button>
        </div>
      </div>

      {/* Scrollable Content */}
      <div className="flex-1 overflow-y-auto">
        <div className="max-w-4xl mx-auto px-4 py-6 sm:px-6 lg:px-8 sm:py-8">
          
          {/* Header */}
          <div className="mb-6">
            <h1 className="text-2xl sm:text-3xl font-extrabold text-brand-primary font-montserrat">
              {title}
            </h1>
            <p className="mt-2 text-sm sm:text-base text-slate-600 font-montserrat">
              {description}
            </p>
          </div>

          {/* Global error */}
          {error && (
            <div className="bg-red-50 border-l-4 border-red-500 rounded-xl p-4 mb-6">
              <div className="flex items-start gap-3">
                <AlertTriangle className="w-6 h-6 text-red-600 flex-shrink-0 mt-0.5" />
                <div>
                  <h3 className="text-sm font-bold text-red-900 font-montserrat mb-1">
                    Error
                  </h3>
                  <p className="text-sm text-red-700 font-montserrat">
                    {error}
                  </p>
                </div>
              </div>
            </div>
          )}

          {/* Step content */}
          <div className="space-y-6">
            {/* Step 1: Date Selection */}
            {currentStep === 'date' && (
              <DateSelector
                selectedDate={selectedDate}
                onDateChange={handleDateChange}
              />
            )}

            {/* Step 2: File Upload */}
            {(currentStep === 'upload' || currentStep === 'validating' || currentStep === 'review' || currentStep === 'submitting' || currentStep === 'complete') && selectedDate && (
              <FileUploader
                onFileSelect={handleFileSelect}
                selectedFile={selectedFile}
                onClearFile={handleClearFile}
                disabled={currentStep === 'validating' || currentStep === 'submitting'}
                userProducts={userProducts}
              />
            )}

            {/* Step 3: Validation Progress */}
            {currentStep === 'validating' && (
              <ValidationProgress
                totalRows={validatedDeliveries.length || 0}
                processedRows={validatedDeliveries.length || 0}
              />
            )}

            {/* Step 4: Review Deliveries */}
            {currentStep === 'review' && validatedDeliveries.length > 0 && (
              <DeliveriesReview
                validatedDeliveries={validatedDeliveries}
                onUpdateDelivery={handleUpdateDelivery}
                onDeleteDelivery={handleDeleteDelivery}
                onProceed={handleProceed}
                onChangeDate={() => setCurrentStep('date')}
                selectedDate={selectedDate}
                validDistricts={validDistricts}
                userProducts={userProducts}
              />
            )}

            {/* Step 5: Submission Progress */}
            {currentStep === 'submitting' && (
              <SubmissionProgress
                total={submissionProgress.total}
                created={submissionProgress.created}
                messagesSent={submissionProgress.messagesSent}
                currentStage={submissionProgress.currentStage}
              />
            )}

            {/* Step 6: Complete */}
            {currentStep === 'complete' && submissionResult && (
              <div className="bg-white rounded-2xl border-2 border-brand-green p-6 sm:p-8">
                <div className="flex flex-col items-center text-center">
                  <div className="w-20 h-20 rounded-full bg-gradient-to-br from-brand-green to-brand-primary flex items-center justify-center mb-6">
                    <PackageCheck className="w-10 h-10 text-white" />
                  </div>

                  <h2 className="text-2xl font-extrabold text-brand-green font-montserrat mb-2">
                    ¡Carga Completada!
                  </h2>
                  
                  <p className="text-sm text-slate-600 font-montserrat mb-6">
                    {submissionResult.created} entregas creadas exitosamente
                  </p>

                  {/* Stats */}
                  <div className="w-full max-w-md grid grid-cols-2 gap-3 mb-6">
                    <div className="bg-brand-green/10 rounded-xl p-4 border border-brand-green/30">
                      <p className="text-xs text-slate-600 font-montserrat mb-1">Entregas creadas</p>
                      <p className="text-2xl font-bold text-brand-green font-montserrat">
                        {submissionResult.created}
                      </p>
                    </div>
                    <div className="bg-brand-green/10 rounded-xl p-4 border border-brand-green/30">
                      <p className="text-xs text-slate-600 font-montserrat mb-1">Mensajes enviados</p>
                      <p className="text-2xl font-bold text-brand-green font-montserrat">
                        {submissionResult.send_ok}
                      </p>
                    </div>
                  </div>

                  {/* Failed messages */}
                  {submissionResult.send_failed_count > 0 && (
                    <div className="w-full max-w-md bg-[#E6B43C]/10 border-l-4 border-[#E6B43C] rounded-lg p-4 mb-6">
                      <p className="text-sm font-semibold text-slate-700 font-montserrat">
                        ⚠️ {submissionResult.send_failed_count} mensaje(s) no se pudieron enviar
                      </p>
                    </div>
                  )}

                  {/* Actions */}
                  <div className="w-full max-w-md flex flex-col gap-3">
                    <button
                      onClick={() => setActiveComponent(returnComponent)}
                      className="w-full px-4 py-3 bg-brand-primary text-white rounded-xl font-semibold hover:bg-brand-primary/90 transition-colors font-montserrat"
                    >
                      {successPrimaryLabel}
                    </button>
                    <button
                      onClick={() => setActiveComponent(controlTowerComponent)}
                      className="w-full px-4 py-3 border-2 border-slate-300 text-slate-700 rounded-xl font-semibold hover:bg-slate-50 transition-colors font-montserrat"
                    >
                      {controlTowerLabel}
                    </button>
                    <button
                      onClick={handleReset}
                      className="w-full px-4 py-3 text-brand-primary rounded-xl font-semibold hover:bg-brand-primary/5 transition-colors font-montserrat"
                    >
                      Crear Más Entregas
                    </button>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

export default MasivoWizard;
